import { Info } from "lucide-react";
import type { AutoBatchResponse, AutoCandidate } from "@opus-dt/shared-types";
import { Pill } from "@/components/ui/pill";
import { formatDateTime } from "@/lib/display-labels";
import { fmtMetric, objDelta, objMetric } from "@/features/simulations/auto/metrics";
import { answerMarks } from "./answer-marks";

interface Props {
  batch: AutoBatchResponse;
  /** Melhor candidato (ou a própria base, quando nenhuma variação superou). */
  winner: AutoCandidate | null;
  baseline: AutoCandidate | null;
  question: string;
}

// A RESPOSTA em destaque. Antes o resultado morava numa coluna de 300px com cinco
// blocos empilhados, todos com o mesmo peso visual: o número que respondia a pergunta disputava
// espaço com premissas, confiança e comparação. Aqui há um único elemento de destaque, e a
// pergunta aparece acima dele para o número nunca ser lido fora de contexto.
export function AnswerBlock({ batch, winner, baseline, question }: Props) {
  const metric = winner ? objMetric(winner, batch) : null;
  const baseMetric = baseline ? objMetric(baseline, batch) : null;
  const delta = winner ? objDelta(winner, baseline, batch) : null;
  const marks = answerMarks(batch, winner);

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-muted-foreground">{question}</p>

      <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
        <div>
          <p className="font-mono text-[30px] font-medium leading-none tracking-[-0.03em]">
            {metric ? fmtMetric(metric) : "—"}
          </p>
          <p className="mt-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            {metric?.label ?? "Aguardando resultado"}
          </p>
        </div>

        {baseMetric && baseline !== winner ? (
          <div>
            <p className="font-mono text-[20px] font-medium leading-none tracking-[-0.03em] text-muted-foreground">
              {fmtMetric(baseMetric)}
            </p>
            <p className="mt-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              Configuração atual
            </p>
          </div>
        ) : null}

        {delta ? (
          <Pill tone={delta.positive ? "success" : "danger"} withDot={false}>
            {delta.text}
          </Pill>
        ) : null}

        {/* O número acima vive no eixo OPERACIONAL: só conta horas de
            expediente. Numa linha de 6 h/dia, "11,3 h" são dois dias de trabalho, e quem
            programa a fábrica precisa da data, não do acumulado. */}
        {winner?.targetReachedAt ? (
          <div>
            <p className="font-mono text-[20px] font-medium leading-none tracking-[-0.03em]">
              {formatDateTime(winner.targetReachedAt)}
            </p>
            <p className="mt-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              Fica pronta em
            </p>
          </div>
        ) : null}
      </div>

      {marks.length > 0 ? (
        <ul className="space-y-1.5 border-t pt-2.5">
          {marks.map((m) => (
            <li key={m.kind} className="flex items-start gap-2 text-[12px] leading-relaxed">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span>
                <span className="font-medium">{m.label}.</span>{" "}
                <span className="text-muted-foreground">{m.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
